import React from "react";
import styled from "styled-components";

import {
  BlockRemoveButton,
  ReferencePickerPlugin,
  UnstyledAnchor,
} from "@adsk/acc-reference-picker";
import Badge from "@adsk/alloy-react-badge";
import { useLocale } from "@adsk/alloy-react-locale";
import theme from "@adsk/alloy-react-theme";
import { OverflowTooltip } from "@adsk/alloy-react-tooltip";

import CategoryName from "../../components/CategoryName";
import StatusWithProgressDisplay from "../../components/DetailsTab/StatusWithProgressDisplay";
import {
  AssetWithCategoryAndStatus,
  statusColors,
  UNSPECIFIED_STATUS_COLOR,
} from "../../models";
import {
  getAssetPercentComplete,
  isPercentCompleteStatus,
} from "../../models/completionTracking";
import { useFeatureFlag } from "../../providers/FeatureFlagProvider";
import getAssetUrl from "../helpers/getAssetUrl";

const Container = styled(UnstyledAnchor)<{ $isDeleted?: boolean }>`
  align-items: center;
  background-color: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.charcoal100};
  cursor: ${({ $isDeleted }) => ($isDeleted ? "default" : "pointer")};
  display: flex;
  justify-content: space-between;
  padding: 14px;

  &:hover .AssetsEntityBlock__remove-button {
    visibility: visible;
  }
`;

const Details = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-height: 0;
  min-width: 0;
`;

const NameRow = styled.div`
  align-items: center;
  display: flex;
  min-width: 0;
`;

const Name = styled.div(theme.typography.heading4);

const DeletedBadge = styled(Badge).attrs({
  size: Badge.SIZES.SMALL,
  color: theme.colors.red500,
})`
  flex-shrink: 0;
  margin-left: 8px;
`;

const Status = styled.div({
  marginTop: "4px",
});

const AssetsEntityBlock: ReferencePickerPlugin<AssetWithCategoryAndStatus>["EntityBlock"] =
  ({ entity: asset, projectId, onRemoveClick, onReferenceClickAnalytics }) => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const isDeleted = !asset.isActive;
    const isPercentCompleteEnabled = useFeatureFlag(
      "enable-asset-status-percent-complete"
    );
    const isValidStatus =
      asset.category?.statusSetId === asset.status?.statusStepSetId;
    const statusColor =
      asset.status && isValidStatus && asset.status.color
        ? statusColors[asset.status.color]
        : UNSPECIFIED_STATUS_COLOR;
    const resolvedStatusLabel =
      isValidStatus && asset.status ? asset.status.label : undefined;
    const percentComplete =
      isPercentCompleteEnabled &&
      isValidStatus &&
      isPercentCompleteStatus(asset.status)
        ? getAssetPercentComplete(asset)
        : null;

    return (
      <Container
        onClick={isDeleted ? undefined : onReferenceClickAnalytics}
        href={isDeleted ? undefined : getAssetUrl(projectId, asset.id)}
        $isDeleted={isDeleted}
      >
        <Details>
          <NameRow>
            <Name>
              <OverflowTooltip>{asset.clientAssetId}</OverflowTooltip>
            </Name>
            {isDeleted && <DeletedBadge>{T.translate("DELETED")}</DeletedBadge>}
          </NameRow>
          {asset.category && <CategoryName category={asset.category} />}
          <Status>
            <StatusWithProgressDisplay
              hideProgressBar
              isUnspecified={!resolvedStatusLabel}
              percent={percentComplete}
              statusColor={statusColor}
              statusName={resolvedStatusLabel}
              statusNameSize="small"
            />
          </Status>
        </Details>
        {onRemoveClick && (
          <BlockRemoveButton
            className="AssetsEntityBlock__remove-button"
            style={{ visibility: "hidden" }}
            onClick={onRemoveClick}
          />
        )}
      </Container>
    );
  };

export default AssetsEntityBlock;
